import type { NextRequest } from "next/server";
import { NextResponse } from "next/server";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  REFRESH_TOKEN_COOKIE_KEY,
  isTokenExpiredOrNearExpiry,
  mergeCookieHeader,
  refreshTokens,
} from "@trex-2b/libs";
import { AUTH_REQUIRED_ROUTE_PREFIXES, GROWTH_ROUTES } from "../config/routes";

function isAuthRequiredPath(pathname: string): boolean {
  return AUTH_REQUIRED_ROUTE_PREFIXES.some((prefix) =>
    pathname === prefix ? true : pathname.startsWith(prefix),
  );
}

function hasValidAccessSession(access: string | undefined): boolean {
  return !!access && !isTokenExpiredOrNearExpiry(access);
}

export async function trex2bProxy(request: NextRequest) {
  const path = request.nextUrl.pathname;

  const accessToken = request.cookies.get(ACCESS_TOKEN_COOKIE_KEY)?.value;
  const refreshToken = request.cookies.get(REFRESH_TOKEN_COOKIE_KEY)?.value;

  const shouldAttemptRefresh =
    !!refreshToken && (!accessToken || isTokenExpiredOrNearExpiry(accessToken));

  if (shouldAttemptRefresh) {
    const refreshed = await refreshTokens(accessToken ?? "", refreshToken);
    if (refreshed) {
      const requestHeaders = new Headers(request.headers);
      requestHeaders.set(
        "cookie",
        mergeCookieHeader(
          request.headers.get("cookie"),
          refreshed.accessToken,
          refreshed.refreshToken,
        ),
      );

      const response = NextResponse.next({
        request: {
          headers: requestHeaders,
        },
      });

      const cookieOptions = {
        path: "/",
        maxAge: 7 * 24 * 60 * 60,
        sameSite: "lax" as const,
        secure: process.env.NODE_ENV === "production",
      };
      response.cookies.set(
        ACCESS_TOKEN_COOKIE_KEY,
        refreshed.accessToken,
        cookieOptions,
      );
      response.cookies.set(
        REFRESH_TOKEN_COOKIE_KEY,
        refreshed.refreshToken,
        cookieOptions,
      );

      return response;
    }
  }

  if (isAuthRequiredPath(path) && !hasValidAccessSession(accessToken)) {
    const redirectUrl = new URL(GROWTH_ROUTES.ROOT, request.url);
    return NextResponse.redirect(redirectUrl);
  }

  return NextResponse.next();
}
